import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useHuntStore } from '@/stores/huntStore';
import { useAuth } from '@/hooks/useAuth';
import { useSignalR, HuntDeletedMessage, ParticipantJoinedMessage, ParticipantLeftMessage, ParticipantRemovedMessage, ParticipantReadyMessage, HuntStateChangedMessage, HuntStartingMessage, StartCancelledMessage } from '@/hooks/useSignalR';
import { huntService } from '@/services/api/huntService';
import { pinService } from '@/services/api/pinService';
import { Button, StickyFooter, HuntDeletedModal, StartingCountdown } from '@/components/common';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConnectionStatusDot } from '@/components/common/ConnectionStatus';
import { HiderTutorial, isHiderTutorialCompleted, HunterTutorial, isHunterTutorialCompleted } from '@/components/tutorial';
import { Copy, CheckCircle, Circle, LogOut, UserMinus, AlertTriangle, User } from 'lucide-react';
import { ParticipantRole, ParticipantSummary, HuntStatus } from '@gps-hunt/shared';

/**
 * Google "G" logo as an SVG icon
 */
const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

interface KickTarget {
  userId: string;
  displayName: string;
}

/**
 * Hunt Lobby screen - shows participants and allows starting the hunt
 */
export const LobbyScreen: React.FC = () => {
  const { huntId } = useParams<{ huntId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    currentHunt, 
    participants, 
    myRole,
    isLoading,
    error,
    setCurrentHunt,
    setParticipants,
    addParticipant,
    removeParticipant,
    setParticipantReady,
    setMyRole,
    setLoading,
    setError,
    reset,
  } = useHuntStore();

  const [copied, setCopied] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isMarkingReady, setIsMarkingReady] = useState(false);
  const [isGoingBack, setIsGoingBack] = useState(false);
  const [kickTarget, setKickTarget] = useState<KickTarget | null>(null);
  const [kickJustification, setKickJustification] = useState('');
  const [isKicking, setIsKicking] = useState(false);
  const [kickError, setKickError] = useState<string | null>(null);
  const [huntDeletedInfo, setHuntDeletedInfo] = useState<{ huntName: string; message: string } | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const tutorialShownRef = useRef(false);
  
  // Starting countdown state
  const [countdownData, setCountdownData] = useState<{ countdownSeconds: number; startingAt: string } | null>(null);
  const [isCancellingStart, setIsCancellingStart] = useState(false);

  // Show tutorial when role is first determined
  useEffect(() => {
    if (!myRole || tutorialShownRef.current) return;
    
    // Check if tutorial has already been completed for this role
    const isCompleted = myRole === ParticipantRole.HIDER 
      ? isHiderTutorialCompleted() 
      : isHunterTutorialCompleted();
    
    if (!isCompleted) {
      setShowTutorial(true);
      tutorialShownRef.current = true;
    }
  }, [myRole]);

  const handleTutorialClose = () => {
    setShowTutorial(false);
  };

  // Handle hunt state changed event - use ref to avoid dependency issues
  const currentHuntRef = useRef(currentHunt);
  currentHuntRef.current = currentHunt;

  // Handle participant joined event - use store action
  const handleParticipantJoined = useCallback((msg: ParticipantJoinedMessage) => {
    const newParticipant: ParticipantSummary = {
      userId: msg.userId,
      displayName: msg.displayName,
      role: msg.role as ParticipantRole,
      isReady: false,
      isGuest: msg.isGuest ?? true,
    };
    addParticipant(newParticipant);
  }, [addParticipant]);

  // Handle participant left event - use store action
  const handleParticipantLeft = useCallback((msg: ParticipantLeftMessage) => {
    removeParticipant(msg.userId);
  }, [removeParticipant]);

  // Handle participant removed (kicked) event
  const handleParticipantRemoved = useCallback((msg: ParticipantRemovedMessage) => {
    removeParticipant(msg.userId);
    
    // If current user was kicked, redirect to home
    if (user?.uid === msg.userId) {
      const reason = msg.justification 
        ? `Reason: ${msg.justification}` 
        : '';
      alert(`You have been removed from the hunt.${reason ? '\n' + reason : ''}`);
      reset();
      navigate('/', { replace: true });
    }
  }, [removeParticipant, user?.uid, reset, navigate]);

  // Handle participant ready event - use store action
  const handleParticipantReady = useCallback((msg: ParticipantReadyMessage) => {
    setParticipantReady(msg.userId, msg.isReady);
  }, [setParticipantReady]);

  const handleHuntStateChanged = useCallback((msg: HuntStateChangedMessage) => {
    const hunt = currentHuntRef.current;
    if (hunt && hunt.id === msg.huntId) {
      setCurrentHunt({ ...hunt, status: msg.newStatus as HuntStatus });
    }
  }, [setCurrentHunt]);

  // Handle hunt deleted event - clear state immediately so header updates
  const handleHuntDeleted = useCallback((msg: HuntDeletedMessage) => {
    reset(); // Clear hunt state immediately to remove join code from header
    setHuntDeletedInfo({ huntName: msg.huntName, message: msg.message });
  }, [reset]);

  // Handle hunt starting event - show countdown modal
  const handleHuntStarting = useCallback((msg: HuntStartingMessage) => {
    setCountdownData({
      countdownSeconds: msg.countdownSeconds,
      startingAt: msg.startingAt,
    });
  }, []);

  // Handle start cancelled event - hide countdown modal
  const handleStartCancelled = useCallback((msg: StartCancelledMessage) => {
    setCountdownData(null);
    setIsCancellingStart(false);
    // Update the cancelling participant's ready status to false (will also come via participantReady event)
    setParticipantReady(msg.cancelledBy, false);
  }, [setParticipantReady]);

  // Memoize SignalR options to pass handlers
  const signalROptions = useMemo(() => ({
    onParticipantJoined: handleParticipantJoined,
    onParticipantLeft: handleParticipantLeft,
    onParticipantRemoved: handleParticipantRemoved,
    onParticipantReady: handleParticipantReady,
    onHuntStateChanged: handleHuntStateChanged,
    onHuntDeleted: handleHuntDeleted,
    onHuntStarting: handleHuntStarting,
    onStartCancelled: handleStartCancelled,
  }), [handleParticipantJoined, handleParticipantLeft, handleParticipantRemoved, handleParticipantReady, handleHuntStateChanged, handleHuntDeleted, handleHuntStarting, handleStartCancelled]);

  // SignalR connection with handlers
  const { connectionStatus, isConnected, connect, disconnect } = useSignalR(signalROptions);

  // Connect to SignalR
  useEffect(() => {
    if (!huntId) return;
    connect(huntId);
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huntId]);

  // Fetch hunt data
  useEffect(() => {
    const fetchHunt = async () => {
      if (!huntId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await huntService.getHunt(huntId);
        setCurrentHunt({
          id: data.hunt.id,
          joinCode: data.hunt.joinCode,
          status: data.hunt.status,
          pinOrder: data.hunt.pinOrder,
          hidersReady: data.hunt.hidersReady,
          completedPinIds: data.hunt.completedPinIds || [],
          createdAt: data.hunt.createdAt,
          startedAt: data.hunt.startedAt,
          completedAt: data.hunt.completedAt,
          createdBy: data.hunt.createdBy,
          name: data.hunt.name,
          description: data.hunt.description,
          creatorDisplayName: data.hunt.creatorDisplayName,
        });
        setParticipants(data.participants);
        setMyRole(data.myRole);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hunt');
      } finally {
        setLoading(false);
      }
    };

    fetchHunt();

    // Poll for updates every 5 seconds
    const interval = setInterval(fetchHunt, 5000);
    return () => clearInterval(interval);
    // Zustand setters are stable references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huntId]);

  // Handle hunt status changes - navigate to appropriate page
  useEffect(() => {
    if (!currentHunt || !myRole || !huntId) return;
    
    // When hunt becomes ACTIVE, navigate everyone to their gameplay page
    if (currentHunt.status === HuntStatus.ACTIVE) {
      if (myRole === ParticipantRole.HUNTER) {
        navigate(`/hunter/${huntId}`);
      } else if (myRole === ParticipantRole.HIDER) {
        // Hiders go to their page to watch progress
        navigate(`/hider/${huntId}`);
      } else if (myRole === ParticipantRole.SPECTATOR) {
        navigate(`/spectator/${huntId}`);
      }
    }
    
    // When hunt is in SETUP, hiders who are NOT ready should go to setup screen
    // Hiders who ARE ready stay in the lobby to wait
    if (currentHunt.status === HuntStatus.SETUP && myRole === ParticipantRole.HIDER) {
      const myParticipant = participants.find(p => p.userId === user?.uid);
      if (!myParticipant?.isReady) {
        navigate(`/hider/${huntId}`);
      }
    }
    // Only re-run when status changes, not when other hunt properties change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHunt?.status, myRole, huntId, navigate, participants, user?.uid]);

  const handleCopyCode = async () => {
    if (currentHunt?.joinCode) {
      await navigator.clipboard.writeText(currentHunt.joinCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLeaveHunt = async () => {
    if (!huntId) return;

    setIsLeaving(true);
    try {
      await huntService.leaveHunt(huntId);
      reset();
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave hunt');
    } finally {
      setIsLeaving(false);
    }
  };

  const handleKickParticipant = (userId: string, displayName: string) => {
    setKickTarget({ userId, displayName });
    setKickJustification('');
    setKickError(null);
  };

  const handleConfirmKick = async () => {
    if (!huntId || !kickTarget) return;
    
    if (kickJustification.trim().length < 5) {
      setKickError('Please provide a reason (at least 5 characters)');
      return;
    }
    
    setIsKicking(true);
    setKickError(null);
    
    try {
      await huntService.kickParticipant(huntId, kickTarget.userId, kickJustification.trim());
      removeParticipant(kickTarget.userId);
      setKickTarget(null);
    } catch (err) {
      setKickError(err instanceof Error ? err.message : 'Failed to remove participant');
    } finally {
      setIsKicking(false);
    }
  };

  const handleCancelKick = () => {
    setKickTarget(null);
    setKickJustification('');
    setKickError(null);
  };

  // Check if current user is the hunt creator
  const isCreator = currentHunt?.createdBy === user?.uid;
  const canKick = isCreator && (currentHunt?.status === HuntStatus.LOBBY || currentHunt?.status === HuntStatus.SETUP);

  const handleBeginSetup = () => {
    // For now, just navigate to hider screen
    // TODO: Implement state transition in Phase 2
    navigate(`/hider/${huntId}`);
  };

  const handleHunterReady = async () => {
    if (!huntId) return;
    
    setIsMarkingReady(true);
    try {
      await pinService.markReady(huntId);
      // The SignalR event will update the participant's ready status
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark ready');
    } finally {
      setIsMarkingReady(false);
    }
  };

  const handleGoBack = async () => {
    if (!huntId) return;
    
    setIsGoingBack(true);
    try {
      await pinService.markNotReady(huntId);
      // Navigate back to hider page to make changes
      navigate(`/hider/${huntId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to go back');
    } finally {
      setIsGoingBack(false);
    }
  };

  // Handle cancel start during countdown
  const handleCancelStart = async () => {
    if (!huntId) return;
    
    setIsCancellingStart(true);
    try {
      await huntService.cancelStart(huntId);
      // SignalR event will clear countdown state
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel start');
      setIsCancellingStart(false);
    }
  };

  // Handle countdown completion - trigger the actual start
  const handleCountdownComplete = useCallback(async () => {
    if (!huntId) return;
    
    try {
      await huntService.startHunt(huntId);
    } catch {
      // If another client already started it or state changed, 
      // the poll will pick up the status change anyway
    }
  }, [huntId]);

  // Group participants by role
  const hiders = participants.filter(p => p.role === ParticipantRole.HIDER);
  const hunters = participants.filter(p => p.role === ParticipantRole.HUNTER);

  // Check if all hiders are ready
  const allHidersReady = hiders.length > 0 && hiders.every(h => h.isReady);
  
  // Check if all hunters are ready
  const allHuntersReady = hunters.length > 0 && hunters.every(h => h.isReady);
  
  // Check if ALL participants are ready
  const allParticipantsReady = allHidersReady && allHuntersReady;
  
  // Check if current user (hunter) is ready
  const currentUserParticipant = participants.find(p => p.userId === user?.uid);
  const isCurrentUserReady = currentUserParticipant?.isReady ?? false;

  // Fallback: If hunt is in STARTING state but countdown isn't showing
  // (e.g., page was refreshed during countdown), trigger start immediately.
  // Also handles edge cases where everyone is ready but state didn't transition.
  useEffect(() => {
    if (!huntId || !currentHunt) return;
    
    // If in STARTING state but no countdown shown, call startHunt
    if (currentHunt.status === HuntStatus.STARTING && !countdownData) {
      const triggerStart = async () => {
        try {
          await huntService.startHunt(huntId);
        } catch {
          // If another client already started it, the API will return an error
          // but the poll will pick up the status change anyway
        }
      };
      
      // Small delay to allow SignalR to catch up
      const timeout = setTimeout(triggerStart, 1000);
      return () => clearTimeout(timeout);
    }
    
    // Legacy fallback: All ready but still in READY state - trigger markReady
    if (currentHunt.status === HuntStatus.READY && allParticipantsReady) {
      // Don't auto-trigger - this state shouldn't persist long
      // The server should have transitioned to STARTING when all marked ready
      return;
    }
    // Only re-run when relevant state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huntId, currentHunt?.status, allParticipantsReady, countdownData]);

  if (isLoading && !currentHunt) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error && !currentHunt) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="secondary" onClick={() => navigate('/')}>
          Go Home
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-surface-light">
        <span className="text-text-secondary text-sm">Share Code</span>
        <div className="flex items-center gap-2">
          <ConnectionStatusDot status={connectionStatus} />
          <span className={`text-xs ${isConnected ? 'text-success' : 'text-red-500'} capitalize`}>
            {connectionStatus}
          </span>
        </div>
      </div>

      {/* Join Code */}
      <div className="p-6 text-center border-b border-surface-light">
        <p className="text-text-secondary text-sm mb-2">Hunt Code</p>
        <div className="flex items-center justify-center gap-3">
          <span className="text-4xl font-mono font-bold text-text-primary tracking-widest">
            {currentHunt?.joinCode || '----'}
          </span>
          <button
            onClick={handleCopyCode}
            className="p-2 rounded-lg bg-surface hover:bg-surface-light transition-colors"
          >
            {copied ? (
              <CheckCircle className="w-5 h-5 text-success" />
            ) : (
              <Copy className="w-5 h-5 text-text-secondary" />
            )}
          </button>
        </div>
      </div>

      {/* Participants */}
      <div className="flex-1 p-4 overflow-y-auto pb-40">
        <h2 className="text-lg font-semibold text-text-primary mb-4">
          PARTICIPANTS ({participants.length})
        </h2>

        <div className="space-y-3">
          {/* Hiders */}
          {hiders.map((participant) => (
            <ParticipantCard 
              key={participant.userId} 
              participant={participant}
              isCurrentUser={participant.userId === user?.uid}
              canKick={canKick && participant.userId !== user?.uid}
              onKick={() => handleKickParticipant(participant.userId, participant.displayName)}
            />
          ))}

          {/* Hunters */}
          {hunters.map((participant) => (
            <ParticipantCard 
              key={participant.userId} 
              participant={participant}
              isCurrentUser={participant.userId === user?.uid}
              canKick={canKick && participant.userId !== user?.uid}
              onKick={() => handleKickParticipant(participant.userId, participant.displayName)}
            />
          ))}
        </div>
      </div>

      {/* Actions - Sticky Footer */}
      <StickyFooter>
        {myRole === ParticipantRole.HIDER && (
          <>
            {currentHunt?.status === HuntStatus.READY ? (
              <p className="text-center text-text-secondary">
                Waiting for hunters...
              </p>
            ) : isCurrentUserReady ? (
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                onClick={handleGoBack}
                isLoading={isGoingBack}
                disabled={isGoingBack}
              >
                Go Back And Make Changes
              </Button>
            ) : (
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleBeginSetup}
              >
                BEGIN SETUP →
              </Button>
            )}
          </>
        )}

        {myRole === ParticipantRole.HUNTER && (
          <>
            {!allHidersReady ? (
              <p className="text-center text-text-secondary">
                Waiting for hiders to place pins...
              </p>
            ) : isCurrentUserReady ? (
              <p className="text-center text-text-secondary">
                Waiting for other players...
              </p>
            ) : (
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleHunterReady}
                isLoading={isMarkingReady}
                disabled={isMarkingReady}
              >
                I&apos;M READY
              </Button>
            )}
          </>
        )}

        <button
          onClick={handleLeaveHunt}
          disabled={isLeaving}
          className="w-full py-3 text-text-secondary hover:text-red-500 transition-colors flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" />
          {isLeaving ? 'Leaving...' : 'Leave Hunt'}
        </button>
      </StickyFooter>
      
      {/* Kick confirmation modal */}
      {kickTarget && (
        <KickConfirmModal
          target={kickTarget}
          justification={kickJustification}
          setJustification={setKickJustification}
          error={kickError}
          isKicking={isKicking}
          onConfirm={handleConfirmKick}
          onCancel={handleCancelKick}
        />
      )}

      {/* Hunt deleted modal */}
      {huntDeletedInfo && (
        <HuntDeletedModal
          huntName={huntDeletedInfo.huntName}
          message={huntDeletedInfo.message}
          onDismiss={() => {
            // State already cleared by signal handler
            navigate('/', { replace: true });
          }}
        />
      )}

      {/* Hunt starting countdown */}
      {countdownData && (
        <StartingCountdown
          countdownSeconds={countdownData.countdownSeconds}
          startingAt={countdownData.startingAt}
          onCountdownComplete={handleCountdownComplete}
          onCancel={handleCancelStart}
          isCancelling={isCancellingStart}
        />
      )}

      {/* Tutorials */}
      {myRole === ParticipantRole.HIDER && (
        <HiderTutorial
          isOpen={showTutorial}
          onClose={handleTutorialClose}
          onComplete={handleTutorialClose}
        />
      )}
      {myRole === ParticipantRole.HUNTER && (
        <HunterTutorial
          isOpen={showTutorial}
          onClose={handleTutorialClose}
          onComplete={handleTutorialClose}
        />
      )}
    </div>
  );
};

interface ParticipantCardProps {
  participant: ParticipantSummary;
  isCurrentUser: boolean;
  canKick?: boolean;
  onKick?: () => void;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({ participant, isCurrentUser, canKick, onKick }) => {
  // Get initials from display name
  const initials = participant.displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={`flex items-center p-4 rounded-xl bg-surface ${isCurrentUser ? 'ring-2 ring-primary ring-opacity-50' : ''}`}>
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
        {initials}
      </div>

      {/* Name */}
      <div className="flex-1 ml-3 min-w-0">
        <div className="flex items-center gap-1.5">
          {/* Auth type icon - default to guest if isGuest is undefined */}
          {participant.isGuest === false ? (
            <span title="Google Account" className="flex-shrink-0">
              <GoogleIcon className="w-3.5 h-3.5" />
            </span>
          ) : (
            <span title="Guest" className="flex-shrink-0">
              <User className="w-3.5 h-3.5 text-text-secondary" />
            </span>
          )}
          <span className="text-text-primary font-medium truncate">
            {participant.displayName}
            {isCurrentUser && <span className="text-text-secondary ml-1">(you)</span>}
          </span>
        </div>
      </div>

      {/* Role badge */}
      <span className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium uppercase ${
        participant.role === ParticipantRole.HIDER 
          ? 'bg-primary/20 text-primary' 
          : 'bg-accent/20 text-accent'
      }`}>
        {participant.role}
      </span>

      {/* Ready indicator */}
      <div className="ml-3 flex-shrink-0">
        {participant.isReady ? (
          <CheckCircle className="w-5 h-5 text-success" />
        ) : (
          <Circle className="w-5 h-5 text-text-secondary" />
        )}
      </div>

      {/* Kick button (for creator only) */}
      {canKick && (
        <button
          onClick={onKick}
          className="ml-2 p-1 flex-shrink-0 text-text-secondary hover:text-red-500 transition-colors"
          title="Remove participant"
        >
          <UserMinus className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

/**
 * Kick confirmation modal
 */
const KickConfirmModal: React.FC<{
  target: KickTarget;
  justification: string;
  setJustification: (value: string) => void;
  error: string | null;
  isKicking: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ target, justification, setJustification, error, isKicking, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-secondary rounded-xl w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Remove Participant</h2>
            <p className="text-sm text-text-secondary">
              Kick {target.displayName} from the hunt?
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm text-text-secondary mb-2">
            Reason for removal <span className="text-red-400">*</span>
          </label>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="e.g., Inappropriate behavior, not following rules..."
            className="w-full px-4 py-3 bg-background rounded-lg border border-white/10 
                       text-white placeholder-text-secondary resize-none
                       focus:outline-none focus:border-primary/50"
            rows={3}
            maxLength={200}
          />
          <div className="flex justify-between mt-1">
            {error && <span className="text-xs text-red-400">{error}</span>}
            <span className="text-xs text-text-secondary ml-auto">
              {justification.length}/200
            </span>
          </div>
          
          {/* Quick fill button */}
          <button
            type="button"
            onClick={() => setJustification('*** Inactive Participant ***')}
            className="mt-2 px-3 py-1.5 text-xs bg-yellow-500/20 text-yellow-400 
                       rounded-lg hover:bg-yellow-500/30 transition-colors"
          >
            Quick: Inactive Participant
          </button>
        </div>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            className="flex-1"
            disabled={isKicking}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isKicking || justification.trim().length < 5}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            {isKicking ? 'Removing...' : 'Remove'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LobbyScreen;
